<script setup>
import { computed } from 'vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: true,
  },
  sections: {
    type: Array,
    required: true,
  },
  activeSection: {
    type: String,
    required: true,
  },
  firstRowSize: {
    type: Number,
    default: 4,
  },
});

const emit = defineEmits(['sectionChanged']);

const firstRowSections = computed(() =>
  props.sections.slice(0, props.firstRowSize)
);
const secondRowSections = computed(() =>
  props.sections.slice(props.firstRowSize)
);
const activeSectionIndex = computed(() =>
  props.sections.findIndex(section => section.key === props.activeSection)
);
const firstRowActiveTab = computed(() =>
  activeSectionIndex.value >= 0 && activeSectionIndex.value < props.firstRowSize
    ? activeSectionIndex.value
    : -1
);
const secondRowActiveTab = computed(() =>
  activeSectionIndex.value >= props.firstRowSize
    ? activeSectionIndex.value - props.firstRowSize
    : -1
);
const activeSectionDetails = computed(
  () =>
    props.sections.find(section => section.key === props.activeSection) ||
    props.sections[0]
);
</script>

<template>
  <section class="grid w-full min-w-0 gap-5">
    <header class="flex min-w-0 flex-wrap items-start justify-between gap-3">
      <div class="grid min-w-0 gap-2">
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ title }}
        </h2>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ description }}
        </p>
      </div>
      <slot name="actions" />
    </header>

    <div v-if="sections.length" class="grid justify-start gap-2">
      <TabBar
        :tabs="firstRowSections"
        :initial-active-tab="firstRowActiveTab"
        @tab-changed="emit('sectionChanged', $event)"
      />
      <TabBar
        v-if="secondRowSections.length"
        :tabs="secondRowSections"
        :initial-active-tab="secondRowActiveTab"
        @tab-changed="emit('sectionChanged', $event)"
      />
    </div>

    <div
      v-if="activeSectionDetails"
      class="flex min-w-0 items-start gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-4"
      data-testid="cortex-section-guide-card"
    >
      <span
        class="mt-0.5 size-4 shrink-0 text-n-brand"
        :class="activeSectionDetails.icon"
      />
      <div class="min-w-0">
        <h3 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ activeSectionDetails.label }}
        </h3>
        <p class="mb-0 mt-1 text-label-small leading-4 text-n-slate-11">
          {{ activeSectionDetails.description }}
        </p>
      </div>
    </div>

    <slot />
  </section>
</template>
